'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel, { formControlLabelClasses } from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import { useSchedulerStoreContext } from '@mui/x-scheduler-internals/use-scheduler-store-context';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { schedulerEventSelectors, schedulerOccurrencePlaceholderSelectors, schedulerOtherSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventDialogStyledContext } from "./EventDialogStyledContext.mjs";
import { computeRange, hasProp } from "./utils.mjs";
import ResourceAndColorSection from "./ResourceAndColorSection.mjs";
import { EventDialogTabPanel, EventDialogTabContent } from "./EventDialogTabPanel.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SectionFieldset = styled('fieldset', {
  name: 'MuiEventDialog',
  slot: 'SectionFieldset'
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  border: 0,
  margin: 0,
  padding: 0,
  minInlineSize: 'min-content'
}));
const SectionHeaderTitle = styled('legend', {
  name: 'MuiEventDialog',
  slot: 'SectionHeaderTitle'
})(({
  theme
}) => _extends({}, theme.typography.subtitle2, {
  padding: 0,
  marginBlockEnd: theme.spacing(2),
  textTransform: 'uppercase',
  color: (theme.vars || theme).palette.text.secondary
}));
const DateTimeFieldsContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'DateTimeFieldsContainer'
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3)
}));
const DateTimeFieldsRow = styled('div', {
  name: 'MuiEventDialog',
  slot: 'DateTimeFieldsRow'
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  '& > :first-of-type': {
    flex: '1.5 1 0%'
  },
  '& > :nth-of-type(2)': {
    flex: '1 1 0%'
  }
}));
const AllDayFormControlLabel = styled(FormControlLabel, {
  name: 'MuiEventDialog',
  slot: 'AllDayFormControlLabel'
})({
  width: '100%',
  justifyContent: 'space-between',
  [`&.${formControlLabelClasses.root}`]: {
    marginLeft: 0
  }
});
export function GeneralTab(props) {
  const {
    occurrence,
    errors,
    setErrors,
    controlled,
    setControlled,
    value
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const {
    schedulerId,
    classes,
    localeText
  } = useEventDialogStyledContext();
  const store = useSchedulerStoreContext();

  // Selector hooks
  const displayTimezone = useStore(store, schedulerOtherSelectors.displayTimezone);
  const shouldEventRequireResource = useStore(store, schedulerOtherSelectors.shouldEventRequireResource);
  const isPropertyReadOnly = useStore(store, schedulerEventSelectors.isPropertyReadOnly, occurrence.id);
  const rawPlaceholder = useStore(store, schedulerOccurrencePlaceholderSelectors.value);
  function pushPlaceholder(next) {
    if (rawPlaceholder?.type !== 'creation') {
      return;
    }
    const {
      start,
      end,
      surfaceType
    } = computeRange(adapter, next, displayTimezone);
    const surfaceTypeToUse = rawPlaceholder.lockSurfaceType ? rawPlaceholder.surfaceType : surfaceType;
    store.setOccurrencePlaceholder({
      type: 'creation',
      surfaceType: surfaceTypeToUse,
      resourceId: next.resourceId,
      start,
      end,
      lockSurfaceType: rawPlaceholder.lockSurfaceType
    });
  }
  const createHandleChangeDateOrTimeField = field => event => {
    const inputValue = event.currentTarget.value;
    setErrors({});
    const newState = _extends({}, controlled, {
      [field]: inputValue
    });
    pushPlaceholder(newState);
    setControlled(newState);
  };
  const handleToggleAllDay = checked => {
    const newState = _extends({}, controlled, {
      allDay: checked
    });
    pushPlaceholder(newState);
    setControlled(newState);
  };
  const handleResourceChange = newResource => {
    const nextErrors = _extends({}, errors);
    delete nextErrors.resource;
    setErrors(nextErrors);
    const newState = _extends({}, controlled, {
      resourceId: newResource
    });
    pushPlaceholder(newState);
    setControlled(newState);
  };
  const handleColorChange = newColor => {
    const newState = _extends({}, controlled, {
      color: newColor
    });
    pushPlaceholder(newState);
    setControlled(newState);
  };
  return /*#__PURE__*/_jsx(EventDialogTabPanel, {
    role: "tabpanel",
    id: `${schedulerId}-general-tabpanel`,
    "aria-labelledby": `${schedulerId}-general-tab`,
    className: classes.eventDialogTabPanel,
    hidden: value !== 'general',
    children: /*#__PURE__*/_jsxs(EventDialogTabContent, {
      className: classes.eventDialogTabContent,
      children: [/*#__PURE__*/_jsxs(SectionFieldset, {
        className: classes.eventDialogSectionFieldset,
        children: [/*#__PURE__*/_jsx(SectionHeaderTitle, {
          className: classes.eventDialogSectionHeaderTitle,
          children: localeText.dateTimeSectionLabel
        }), /*#__PURE__*/_jsxs(DateTimeFieldsContainer, {
          className: classes.eventDialogDateTimeFieldsContainer,
          children: [/*#__PURE__*/_jsxs(DateTimeFieldsRow, {
            className: classes.eventDialogDateTimeFieldsRow,
            children: [/*#__PURE__*/_jsx(TextField, {
              name: "startDate",
              label: localeText.startDateLabel,
              type: "date",
              value: controlled.startDate,
              onChange: createHandleChangeDateOrTimeField('startDate'),
              required: true,
              slotProps: {
                inputLabel: {
                  shrink: true
                },
                input: {
                  readOnly: isPropertyReadOnly('start')
                },
                formHelperText: {
                  role: 'alert'
                }
              },
              error: !!errors.startDate,
              helperText: errors.startDate,
              size: "small"
            }), !controlled.allDay && /*#__PURE__*/_jsx(TextField, {
              name: "startTime",
              label: localeText.startTimeLabel,
              type: "time",
              value: controlled.startTime,
              onChange: createHandleChangeDateOrTimeField('startTime'),
              required: true,
              slotProps: {
                inputLabel: {
                  shrink: true
                },
                input: {
                  readOnly: isPropertyReadOnly('start')
                }
              },
              size: "small"
            })]
          }), /*#__PURE__*/_jsxs(DateTimeFieldsRow, {
            className: classes.eventDialogDateTimeFieldsRow,
            children: [/*#__PURE__*/_jsx(TextField, {
              name: "endDate",
              label: localeText.endDateLabel,
              type: "date",
              value: controlled.endDate,
              onChange: createHandleChangeDateOrTimeField('endDate'),
              required: true,
              slotProps: {
                inputLabel: {
                  shrink: true
                },
                input: {
                  readOnly: isPropertyReadOnly('end')
                }
              },
              size: "small"
            }), !controlled.allDay && /*#__PURE__*/_jsx(TextField, {
              name: "endTime",
              label: localeText.endTimeLabel,
              type: "time",
              value: controlled.endTime,
              onChange: createHandleChangeDateOrTimeField('endTime'),
              required: true,
              slotProps: {
                inputLabel: {
                  shrink: true
                },
                input: {
                  readOnly: isPropertyReadOnly('end')
                }
              },
              size: "small"
            })]
          }), /*#__PURE__*/_jsx(AllDayFormControlLabel, {
            control: /*#__PURE__*/_jsx(Switch, {
              id: `${schedulerId}-enable-all-day-switch`,
              checked: controlled.allDay,
              onChange: event => handleToggleAllDay(event.target.checked),
              disabled: isPropertyReadOnly('allDay')
            }),
            label: localeText.allDayLabel,
            labelPlacement: "start"
          })]
        })]
      }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsxs(SectionFieldset, {
        className: classes.eventDialogSectionFieldset,
        children: [/*#__PURE__*/_jsx(SectionHeaderTitle, {
          className: classes.eventDialogSectionHeaderTitle,
          children: localeText.resourceColorSectionLabel
        }), /*#__PURE__*/_jsx(ResourceAndColorSection, {
          readOnly: isPropertyReadOnly('resource'),
          resourceId: controlled.resourceId,
          onResourceChange: handleResourceChange,
          onColorChange: handleColorChange,
          color: controlled.color,
          error: shouldEventRequireResource && typeof errors.resource === 'string' ? errors.resource : undefined
        })]
      }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(TextField, {
        name: "description",
        label: localeText.descriptionLabel,
        defaultValue: hasProp(occurrence, 'description') ? occurrence.description : '',
        multiline: true,
        rows: 5,
        fullWidth: true,
        slotProps: {
          input: {
            readOnly: isPropertyReadOnly('description')
          }
        }
      })]
    })
  });
}